const urlBase64ToUint8Array = (value) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
};

const request = async (url, csrfToken, payload) => {
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    body: JSON.stringify({ ...payload, csrf_token: csrfToken }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.ok) {
    throw new Error(body?.error || `push_http_${response.status}`);
  }
  return body;
};

const initNotificationSound = () => {
  if (!("serviceWorker" in navigator)) return;
  let audioContext = null;
  let lastTag = "";
  let lastPlayedAt = 0;

  const ensureAudio = () => {
    if (audioContext) return audioContext;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    audioContext = new AudioContextClass();
    return audioContext;
  };
  const primeAudio = () => {
    const context = ensureAudio();
    if (context?.state === "suspended") context.resume().catch(() => {});
  };
  window.addEventListener("pointerdown", primeAudio, { once: true, passive: true });
  window.addEventListener("keydown", primeAudio, { once: true });

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type !== "CEOWEB_NOTIFICATION_SOUND" || document.visibilityState !== "visible") {
      return;
    }
    const tag = String(event.data?.tag || "");
    const now = Date.now();
    if (tag && tag === lastTag && now - lastPlayedAt < 3000) return;
    const context = ensureAudio();
    if (!context || context.state !== "running") return;
    lastTag = tag;
    lastPlayedAt = now;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.22);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.24);
  });
};

export const initWebPush = (context, telemetry = () => {}) => {
  const config = context?.web_push;
  if (
    !config?.enabled ||
    !config.public_key ||
    !context?.csrf_token ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    return null;
  }

  const api = {
    supported: true,
    permission: () => Notification.permission,
    subscription: async () => {
      const registration = await navigator.serviceWorker.ready;
      return registration.pushManager.getSubscription();
    },
    subscribe: async () => {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("push_permission_denied");
      }
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.public_key),
        });
      }
      await request(config.subscribe_url, context.csrf_token, {
        subscription: subscription.toJSON(),
      });
      telemetry("push_subscribed", {});
      return subscription;
    },
    unsubscribe: async () => {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return false;
      await request(config.unsubscribe_url, context.csrf_token, {
        endpoint: subscription.endpoint,
      });
      await subscription.unsubscribe();
      telemetry("push_unsubscribed", {});
      return true;
    },
  };

  window.ceowebPush = api;
  initNotificationSound();
  window.dispatchEvent(new CustomEvent("ceoweb:push-ready", { detail: api }));
  return api;
};
